import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  IAjuste,
  IConciliacao,
  IContaDoPlano,
  ICruzamentos,
  IDemonstracoes,
  IIndicadores,
  IResumo,
  IVerificacao,
  useFechamentoEmpresa,
  useFechamentoSecao,
  useFechamentoConstatacoesDoPeriodo,
} from '../../../../../hooks/useFechamentoContabilMensal';
import {
  aparenciaDoParecer,
  BASE_ROTA,
  brl,
  definirArredondado,
  estaArredondado,
  mesCurto,
  mesLongo,
  periodoPadrao,
  PRIMEIRO_MES,
  REGIME,
  SEVERIDADE,
  tiposNoPeriodo,
} from '../regras';
import type { ITipoNoPeriodo } from '../regras';
import { formatarCnpj } from '../regrasDaBusca';
import { PARECERES_DO_PAINEL } from '../regrasDaCarteira';
import { marcaDoMes } from '../regrasDoFechamento';
import { textoDoRt } from '../regrasDoDetalhe';
import { lerPeriodo, paraUrl, ultimoMesPorAno } from '../regrasDoPeriodoDaEmpresa';
import { AbaAjustes } from '../carteira/detalhe/AbaAjustes';
import { AbaConciliacao } from '../carteira/detalhe/AbaConciliacao';
import { AbaConstatacoes } from '../carteira/detalhe/AbaConstatacoes';
import { AbaCruzamentos } from '../carteira/detalhe/AbaCruzamentos';
import { AbaDemonstracoes } from '../carteira/detalhe/AbaDemonstracoes';
import { AbaIndicadores } from '../carteira/detalhe/AbaIndicadores';
import { AbaResumo, ParecerDaRevisao } from '../carteira/detalhe/AbaResumo';
import { Pendente } from '../carteira/detalhe/DetalheDaEmpresa';
import { SeletorDoPeriodo } from '../carteira/detalhe/SeletorDoPeriodo';
import {
  CABECA_DA_TABELA,
  CAIXA,
  LINK,
  pilula,
  SUB,
  TH,
  TITULO,
} from '../carteira/detalhe/estilos';
import { Circuito } from '../carteira/icones';
import { DetalheDaCarga } from './carga';
import '../carteira.css';

type Tela =
  | 'resumo'
  | 'constatacoes'
  | 'cruzamentos'
  | 'conciliacao'
  | 'demonstracoes'
  | 'indicadores'
  | 'ajustes';
const TELAS: { chave: Tela; nome: string }[] = [
  { chave: 'resumo', nome: 'Resumo' },
  { chave: 'constatacoes', nome: 'Constatações' },
  { chave: 'cruzamentos', nome: 'Cruzamentos' },
  { chave: 'conciliacao', nome: 'Conciliação bancária' },
  { chave: 'demonstracoes', nome: 'Demonstrações' },
  { chave: 'indicadores', nome: 'Indicadores' },
  { chave: 'ajustes', nome: 'Ajustes propostos' },
];
// telas que escrevem contas e precisam do plano para mostrar classificação e nome
const USAM_O_PLANO: Tela[] = ['constatacoes', 'cruzamentos', 'conciliacao', 'demonstracoes'];
// telas que mostram um mês por vez: com período, avisam qual mês está na tela
const UM_MES_POR_VEZ: Tela[] = ['resumo', 'constatacoes', 'cruzamentos', 'conciliacao', 'ajustes'];

const VIDRO: React.CSSProperties = {
  background: 'rgba(255, 255, 255, .62)',
  border: '1px solid rgba(255, 255, 255, .8)',
  borderRadius: 16,
  boxShadow: '0 8px 28px rgba(21, 95, 176, .10)',
  backdropFilter: 'blur(14px)',
};

const ROTULO_DO_TOPO: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: '#52525B',
};

const mesAnterior = (competencia: string) => {
  const [a, m] = competencia.split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
};

function Seta({ dir }: { dir: 'esq' | 'dir' }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#0F172A"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={dir === 'esq' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
    </svg>
  );
}

function Aviso({ titulo, children }: { titulo?: string; children: React.ReactNode }) {
  return (
    <section
      style={{ ...VIDRO, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 4 }}
    >
      {titulo && <div style={TITULO}>{titulo}</div>}
      <span style={{ fontSize: 13, color: '#52525B' }}>{children}</span>
    </section>
  );
}

/** Tipos de constatação de todos os meses do período, com os meses em que aparecem. */
function NoPeriodo({
  rotulo,
  tipos,
  meses,
  competencia,
  onMes,
}: {
  rotulo: string;
  tipos: ITipoNoPeriodo[];
  meses: number;
  competencia: string;
  onMes: (c: string) => void;
}) {
  return (
    <section aria-label="Constatações no período" style={{ ...CAIXA, gap: 8 }}>
      <div style={TITULO}>No período · {rotulo}</div>
      <span style={SUB}>
        Tipos de constatação em todos os meses do período ({meses} mês(es)), com os meses em que
        cada um aparece. A lista detalhada abaixo é a de {mesLongo(competencia)}.
      </span>
      <div
        className="czScroll"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        role="region"
        aria-label="Tipos de constatação no período"
        style={{ overflowX: 'auto' }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 720 }}>
          <thead>
            <tr style={CABECA_DA_TABELA}>
              <th style={{ ...TH, width: 130 }}>Gravidade</th>
              <th style={TH}>Tipo</th>
              <th style={{ ...TH, textAlign: 'right', width: 70 }}>Itens</th>
              <th style={TH}>Meses</th>
              <th style={{ ...TH, textAlign: 'right', width: 160 }}>Valor somado</th>
            </tr>
          </thead>
          <tbody>
            {tipos.map(t => {
              const sev = SEVERIDADE[t.pior];
              return (
                <tr key={t.chave} style={{ borderTop: '1px solid #E4E4E7' }}>
                  <td style={{ padding: '9px 10px' }}>
                    <span style={pilula(sev.suave, sev.cor)}>{sev.rotulo}</span>
                  </td>
                  <td style={{ padding: '9px 10px' }}>{t.chave}</td>
                  <td style={{ padding: '9px 10px', textAlign: 'right' }}>{t.itens}</td>
                  <td style={{ padding: '9px 10px', fontSize: 12 }}>
                    {t.meses.map((m, i) => (
                      <React.Fragment key={m}>
                        {i > 0 && ' · '}
                        <button
                          type="button"
                          style={{ ...LINK, textDecoration: 'none' }}
                          onClick={() => onMes(m)}
                        >
                          {mesCurto(m)}
                        </button>
                      </React.Fragment>
                    ))}
                  </td>
                  <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                    {t.total ? brl(t.total) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function FechamentoContabilEmpresaPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const empresaId = Number(params.get('empresa')) || undefined;
  const padrao = useMemo(() => periodoPadrao(new Date()), []);
  const competencia = params.get('comp') || padrao.fim;
  const tela = (TELAS.find(t => t.chave === params.get('tela'))?.chave ?? 'resumo') as Tela;
  const ini = `${competencia.slice(0, 4)}-01`;
  const fim = `${competencia.slice(0, 4)}-12`;

  const { data: noPeriodo } = useFechamentoEmpresa(empresaId, ini, fim);
  // o filtro de período enxerga todos os meses revisados da empresa, de todos os anos
  const { data: todosOsMeses } = useFechamentoEmpresa(empresaId, PRIMEIRO_MES, padrao.fim);
  const mesesRevisados = (todosOsMeses?.empresa?.meses ?? [])
    .filter(m => m.tem_detalhe)
    .map(m => m.competencia);
  // início do período: o fim é sempre a competência aberta
  const deNaUrl = params.get('de') || competencia;
  const de = deNaUrl <= competencia ? deNaUrl : competencia;
  // anos e quinzenas do botão Período; as abas de um mês só olham `de` e `comp`
  const periodo = lerPeriodo(params, de, competencia);
  const ultimos = ultimoMesPorAno(mesesRevisados);
  const resumo = useFechamentoSecao<IResumo>('resumo', empresaId, competencia);
  const anterior = useFechamentoSecao<IResumo>(
    'resumo',
    empresaId,
    mesAnterior(competencia),
    tela === 'resumo',
  );
  const verificacoes = useFechamentoSecao<IVerificacao[]>(
    'verificacoes',
    empresaId,
    competencia,
    tela === 'constatacoes',
  );
  const plano = useFechamentoSecao<Record<string, IContaDoPlano>>(
    'plano',
    empresaId,
    competencia,
    USAM_O_PLANO.includes(tela) || tela === 'resumo',
  );
  const [arredondado, setArredondado] = useState(estaArredondado);
  const [verCarga, setVerCarga] = useState(false);
  const cruzamentos = useFechamentoSecao<ICruzamentos>(
    'cruzamentos',
    empresaId,
    competencia,
    tela === 'cruzamentos',
  );
  const conciliacao = useFechamentoSecao<IConciliacao>(
    'conciliacao',
    empresaId,
    competencia,
    tela === 'conciliacao',
  );
  const demonstracoes = useFechamentoSecao<IDemonstracoes>(
    'demonstracoes',
    empresaId,
    competencia,
    tela === 'demonstracoes',
  );
  const indicadores = useFechamentoSecao<IIndicadores>(
    'indicadores',
    empresaId,
    competencia,
    tela === 'indicadores',
  );
  const ajustes = useFechamentoSecao<IAjuste[]>(
    'ajustes',
    empresaId,
    competencia,
    tela === 'ajustes',
  );
  const constatacoesDoPeriodo = useFechamentoConstatacoesDoPeriodo(
    empresaId,
    de,
    competencia,
    tela === 'constatacoes',
  );

  // null apaga o parâmetro
  const ir = (mudanca: Record<string, string | null>) => {
    const novo = new URLSearchParams(params);
    Object.entries(mudanca).forEach(([k, v]) => (v == null ? novo.delete(k) : novo.set(k, v)));
    setParams(novo, { replace: true });
  };
  // Troca de ano na régua: mesmo mês no outro ano, sem passar do mês corrente.
  const ano = Number(competencia.slice(0, 4));
  const irParaOAno = (outro: number) => {
    const comp = `${outro}${competencia.slice(4)}`;
    ir({ comp: comp > padrao.fim ? padrao.fim : comp });
  };

  const r = resumo.data?.dados;
  const empresa = r?.empresa;
  const linha = noPeriodo?.empresa;
  const disponivel = !!resumo.data?.disponivel;
  const regime = empresa?.regime_texto ?? REGIME[empresa?.regime ?? ''];
  const info = [
    linha?.external_id,
    empresa?.cnpj ? `CNPJ ${formatarCnpj(empresa.cnpj)}` : null,
    regime,
    de < competencia ? `${mesLongo(de)} a ${mesLongo(competencia)}` : mesLongo(competencia),
    disponivel ? `versão ${resumo.data?.versao}` : null,
    textoDoRt(empresa?.rt),
  ]
    .filter(Boolean)
    .join(' · ');
  const anoMinimo = Number(PRIMEIRO_MES.slice(0, 4));
  const anoMaximo = Number(padrao.fim.slice(0, 4));

  return (
    <div className="fcmCart">
      <div className="brilhoSel" aria-hidden="true">
        <div className="brilhoFixo">
          <div className="bl blBase" />
          <Circuito />
        </div>
      </div>
      <div
        style={{
          maxWidth: 1280,
          margin: '0 auto',
          padding: '28px 32px 48px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        {/* cabeçalho da empresa */}
        <header
          style={{
            position: 'relative',
            zIndex: 20,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 14,
          }}
        >
          <button
            type="button"
            aria-label="Voltar para a carteira"
            onClick={() => navigate(BASE_ROTA)}
            style={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 999,
              border: '1px solid rgba(255, 255, 255, .85)',
              background: 'rgba(255, 255, 255, .7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(21, 95, 176, .10)',
              cursor: 'pointer',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0F172A"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div
            style={{
              flex: '1 1 420px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: '#52525B',
              }}
            >
              Fechamento Contábil Mensal
            </span>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-.01em' }}>
              {empresa?.razao ?? linha?.razao ?? 'Empresa'}
            </h1>
            <span style={{ fontSize: 12.5, color: '#52525B' }}>{info}</span>
            {arredondado && (
              <span style={{ fontSize: 11.5, color: '#52525B' }}>
                Valores arredondados: um total pode diferir em ±1 da soma das linhas.
              </span>
            )}
            {de < competencia && UM_MES_POR_VEZ.includes(tela) && (
              <span style={{ fontSize: 11.5, color: '#B45309' }}>
                Esta aba mostra um mês por vez: {mesLongo(competencia)}, o último do período. O
                período completo aparece em Demonstrações.
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={ROTULO_DO_TOPO}>Valores</span>
              <button
                type="button"
                aria-pressed={arredondado}
                title="Alternar entre centavos e valores arredondados. Só a exibição muda: os totais continuam somados com centavos."
                onClick={() => {
                  definirArredondado(!arredondado);
                  setArredondado(!arredondado);
                }}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 10,
                  border: '1px solid #D4DAE2',
                  background: 'rgba(255, 255, 255, .85)',
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: '#0F172A',
                  cursor: 'pointer',
                }}
              >
                {arredondado ? 'Arredondado' : 'Centavos'}
              </button>
            </div>
            {empresaId != null && mesesRevisados.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={ROTULO_DO_TOPO}>Período</span>
                <SeletorDoPeriodo
                  periodo={periodo}
                  ultimos={ultimos}
                  comQuinzena={tela === 'demonstracoes'}
                  onAplicar={p => ir(paraUrl(p))}
                />
              </div>
            )}
          </div>
        </header>

        {/* régua de competências */}
        {empresaId != null && (
          <section
            aria-label="Competências da empresa"
            style={{
              ...VIDRO,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 6,
              padding: '12px 16px',
            }}
          >
            <span style={{ ...ROTULO_DO_TOPO, fontSize: 11, marginRight: 6 }}>Competência</span>
            <button
              type="button"
              aria-label="Ano anterior"
              disabled={ano <= anoMinimo}
              onClick={() => irParaOAno(ano - 1)}
              style={{
                width: 28,
                height: 28,
                border: 0,
                borderRadius: 8,
                background: 'transparent',
                cursor: ano <= anoMinimo ? 'default' : 'pointer',
                opacity: ano <= anoMinimo ? 0.35 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Seta dir="esq" />
            </button>
            <span style={{ fontSize: 13, fontWeight: 700 }}>{ano}</span>
            <button
              type="button"
              aria-label="Ano seguinte"
              disabled={ano >= anoMaximo}
              onClick={() => irParaOAno(ano + 1)}
              style={{
                width: 28,
                height: 28,
                border: 0,
                borderRadius: 8,
                background: 'transparent',
                cursor: ano >= anoMaximo ? 'default' : 'pointer',
                opacity: ano >= anoMaximo ? 0.35 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 8,
              }}
            >
              <Seta dir="dir" />
            </button>
            {!linha?.meses.length && (
              <span style={{ fontSize: 12, color: '#52525B' }}>
                Nenhuma competência revisada neste ano.
              </span>
            )}
            {(linha?.meses ?? []).map(m => {
              const ap = aparenciaDoParecer(m.status);
              const atual = m.competencia === competencia;
              return (
                <button
                  type="button"
                  key={m.competencia}
                  className="empMesBtn"
                  aria-pressed={atual}
                  title={`${mesLongo(m.competencia)} · ${ap.rotulo} · ${m.criticas} crítica(s) e ${m.altas} alta(s)${marcaDoMes(m)}`}
                  onClick={() =>
                    ir({ comp: m.competencia, de: de <= m.competencia ? de : m.competencia })
                  }
                  style={{
                    position: 'relative',
                    minWidth: 48,
                    height: 30,
                    padding: '0 10px',
                    border: 0,
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    background: ap.cor,
                    opacity: atual ? 1 : 0.78,
                    boxShadow: atual ? '0 0 0 2px #FFFFFF, 0 0 0 4px #0F172A' : 'none',
                  }}
                >
                  {mesCurto(m.competencia)}
                  {m.fechada && (m.alterada ? ' !' : ' ✓')}
                  {m.txt_pendente && (
                    <span
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 10,
                        height: 10,
                        boxSizing: 'border-box',
                        borderRadius: 999,
                        border: '2px solid #FFFFFF',
                        background: '#D97706',
                      }}
                    />
                  )}
                </button>
              );
            })}
            <span
              style={{
                marginLeft: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                fontSize: 11,
                color: '#52525B',
              }}
            >
              {PARECERES_DO_PAINEL.map(p => (
                <span
                  key={p.chave}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: p.cor }} />
                  {p.nome}
                </span>
              ))}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                ✓ fechada ·{' '}
                <span style={{ width: 9, height: 9, borderRadius: 999, background: '#D97706' }} />{' '}
                TXT pendente
              </span>
            </span>
          </section>
        )}

        {/* abas */}
        <nav
          aria-label="Telas da empresa"
          style={{
            display: 'flex',
            gap: 4,
            overflowX: 'auto',
            borderBottom: '1px solid rgba(15, 23, 42, .12)',
          }}
        >
          {TELAS.map(t => {
            const ativa = tela === t.chave;
            return (
              <button
                type="button"
                key={t.chave}
                className="empAba"
                aria-current={ativa ? 'page' : undefined}
                onClick={() => ir({ tela: t.chave })}
                style={{
                  fontWeight: ativa ? 700 : 500,
                  color: ativa ? '#0F172A' : '#52525B',
                  borderBottom: `2px solid ${ativa ? '#1B6FD0' : 'transparent'}`,
                }}
              >
                {t.nome}
              </button>
            );
          })}
        </nav>

        {!empresaId && <Aviso>Abra uma empresa pela visão da carteira.</Aviso>}
        {resumo.isLoading && <Aviso>Carregando a revisão...</Aviso>}
        {resumo.isError && (
          <Aviso titulo={`Sem revisão em ${mesLongo(competencia)}`}>
            A competência ainda não foi apurada para esta empresa, ou você não tem acesso a ela.
          </Aviso>
        )}
        {resumo.data && !disponivel && (
          <Aviso titulo="Detalhe indisponível">
            Detalhe indisponível para a versão {resumo.data.versao}. {resumo.data.motivo}
          </Aviso>
        )}

        {disponivel && r && empresaId != null && (
          <>
            {tela === 'resumo' && (
              <>
                <AbaResumo
                  resumo={r}
                  anterior={anterior.data?.disponivel ? anterior.data.dados : undefined}
                  competencia={competencia}
                  onVerTodas={() => ir({ tela: 'constatacoes' })}
                  completo
                  onCarga={() => setVerCarga(true)}
                />
                <DetalheDaCarga
                  aberto={verCarga}
                  onFechar={() => setVerCarga(false)}
                  empresaId={empresaId}
                  competencia={competencia}
                  razao={r.empresa.razao ?? ''}
                  dre={r.dre}
                  plano={plano.data?.dados}
                />
              </>
            )}
            {tela === 'constatacoes' && (
              <>
                <ParecerDaRevisao resumo={r} />
                {de < competencia && constatacoesDoPeriodo.data && (
                  <NoPeriodo
                    rotulo={`${mesLongo(de)} a ${mesLongo(competencia)}`}
                    tipos={tiposNoPeriodo(constatacoesDoPeriodo.data)}
                    meses={new Set(constatacoesDoPeriodo.data.map(c => c.competencia)).size}
                    competencia={competencia}
                    onMes={m => ir({ comp: m, de: de <= m ? de : m })}
                  />
                )}
                {verificacoes.data?.dados ? (
                  <AbaConstatacoes itens={verificacoes.data.dados} plano={plano.data?.dados} />
                ) : (
                  <Pendente secao={verificacoes} nome="constatações" />
                )}
              </>
            )}
            {tela === 'cruzamentos' &&
              (cruzamentos.data?.dados ? (
                <AbaCruzamentos
                  dados={cruzamentos.data.dados}
                  materialidade={r.materialidade}
                  plano={plano.data?.dados}
                />
              ) : (
                <Pendente secao={cruzamentos} nome="cruzamentos" />
              ))}
            {tela === 'conciliacao' &&
              (conciliacao.data?.dados ? (
                <AbaConciliacao
                  dados={conciliacao.data.dados}
                  plano={plano.data?.dados}
                  empresaId={empresaId}
                  competencia={competencia}
                />
              ) : (
                <Pendente secao={conciliacao} nome="conciliação" />
              ))}
            {tela === 'demonstracoes' && empresaId != null && (
              <AbaDemonstracoes
                empresaId={empresaId}
                periodo={periodo}
                plano={plano.data?.dados}
                secao={demonstracoes.data?.dados ?? undefined}
                competencia={competencia}
              />
            )}
            {tela === 'indicadores' &&
              (indicadores.data?.dados ? (
                <AbaIndicadores
                  periodoTexto={
                    de < competencia
                      ? `${mesCurto(de)} a ${mesLongo(competencia)}`
                      : mesLongo(competencia)
                  }
                  indicadores={indicadores.data.dados.indicadores.map(i =>
                    // com período, a série de cada indicador mostra só os meses escolhidos
                    de < competencia
                      ? {
                          ...i,
                          serie: i.serie.filter(
                            s => s.competencia >= de && s.competencia <= competencia,
                          ),
                        }
                      : i,
                  )}
                />
              ) : (
                <Pendente secao={indicadores} nome="indicadores" />
              ))}
            {tela === 'ajustes' &&
              (ajustes.data?.dados ? (
                <AbaAjustes ajustes={ajustes.data.dados} lucroDoMes={r.dre.mes.lucro_liquido} />
              ) : (
                <Pendente secao={ajustes} nome="ajustes" />
              ))}
          </>
        )}
      </div>
    </div>
  );
}
